import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { Input } from "@/components/ui/input";
import { hashAll, matchesExpected } from "./hashUtils";
import { utf8Encode } from "@/lib/utf8";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Generates a cryptographic hash (MD5, SHA-1, SHA-256, and others) from text or a file, useful for verifying file integrity or generating a checksum.`,
  how: `Paste text or choose a file and press Generate. You get MD5, SHA-1, SHA-256, SHA-384 and SHA-512 together, and files are read in chunks on your device (they are never uploaded). Paste an expected checksum to see whether it matches.`,
  tip: `MD5 and SHA-1 are considered cryptographically broken for security purposes (though still fine for basic integrity checks) — use SHA-256 or stronger if the hash needs to actually resist tampering, not just detect accidental corruption.`,
};

export default function HashGenerator() {
  const [input, setInput] = useState("");
  const [hashes, setHashes] = useState<Record<string, string>>({});

  const { logUsageNow } = useLogUsageOnAction();

  // Same real, reported gap as the other tools in this round: was
  // useLogToolUsage(true, 3000, { ownActions: true }) -- hardcoded true, not even a real
  // "does a result exist" check, let alone a gate on the click itself.
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [expected, setExpected] = useState("");
  const [error, setError] = useState("");

  const generate = async () => {
    setError("");
    if (!input && !file) { setError("Enter some text or choose a file to hash."); return; }
    const allowed = await logUsageNow();
    if (!allowed) return;
    setBusy(true);
    setProgress(0);
    try {
      setHashes(await hashAll(file ?? utf8Encode(input), setProgress));
    } catch {
      setHashes({});
      setError("Could not read that file. It may be locked, moved or too large for the browser to open.");
    } finally {
      setBusy(false);
    }
  };

  const match = matchesExpected(hashes, expected);

  return (
    <ToolContainer>
      <div className="space-y-4">
        <div><Label>Input Text (hashed as UTF-8)</Label><Textarea aria-label="Input Text (hashed as UTF-8)" value={input} onChange={e => { setInput(e.target.value); setError(""); }} placeholder="Enter text to hash..." aria-invalid={!!error} /></div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="underline cursor-pointer">
            {file ? `File: ${file.name} (${file.size.toLocaleString()} bytes)` : "Or choose a file to hash"}
            <input type="file" className="hidden" onChange={e => { setFile(e.target.files?.[0] ?? null); setHashes({}); setError(""); }} />
          </label>
          {file && <button className="text-xs text-muted-foreground underline" onClick={() => { setFile(null); setHashes({}); }}>Remove file</button>}
        </div>
        <Button onClick={generate} disabled={busy}>{busy ? `Hashing… ${Math.round(progress * 100)}%` : "Generate Hashes"}</Button>
        {error && <p role="alert" className="text-xs text-destructive mt-1">{error}</p>}
        {Object.keys(hashes).length > 0 && (
          <div className="space-y-3">
            <div>
              <Label>Compare with expected checksum (optional)</Label>
              <Input aria-label="Compare with expected checksum (optional)" value={expected} onChange={e => setExpected(e.target.value)} placeholder="Paste a hash to verify…" className="font-mono text-xs" />
              {match !== null && (
                <p className={`text-sm mt-1 font-medium ${match ? "text-green-600" : "text-destructive"}`}>
                  {match ? `Match (${match})` : "No match for any algorithm"}
                </p>
              )}
            </div>
            {Object.entries(hashes).map(([algo, value]) => (
              <div key={algo} className="p-3 rounded-lg border bg-card">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-muted-foreground">{algo}</span>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { void copyText(value, "Copied!"); }}>Copy</Button>
                </div>
                <code className="text-xs font-mono break-all">{value}</code>
              </div>
            ))}
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
